import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify"; // Importar toast para notificaciones
import UserModal from "./UserModal.tsx";
import { User } from "../types/User.ts";
import { SquarePen, Trash, KeyRound } from "lucide-react";

import { getUsersByCustomerId, createUser, updateUser, deleteUser } from "../api/users.api.js"; // ✨ Importar funciones API (añadido deleteUser)
import { getObras } from "../../obras/api/obras.api.js";
import { Obra } from "../../obras/types/Obra";
import { useParams } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode"; // Importar la librería
import ChangePasswordModal from "./ChangePasswordModal.tsx";


// --- Datos Iniciales con Email y Teléfono Únicos (Mantener para el mock inicial) ---
const initialUsers: User[] = [
  {
    id: "", 
    active: true,
    createdAt: "",
    customerId: "",
    email: "", 
    identifier: "", 
    name: "",
    notificationsType: [],
    password: "",
    permissions: [],
    phoneNumber: "", 
    role: "",
    surname: ""
  },
  
];


// Normaliza `obra`: soporta el formato legacy (string único) además del array actual.
const toObraArray = (obra: unknown): string[] => {
  if (Array.isArray(obra)) return obra;
  if (typeof obra === "string" && obra) return [obra];
  return [];
};

// Función para mapear el rol a una etiqueta legible
const getRoleLabel = (role: string) => {
    switch (role) {
        case "guest":
            return "Invitado";
        case "responsibleUser":
            return "Responsable";
        case "defaultCustomerUser":
            return "Usuario Estándar";
        case "user":
            return "Usuario Estándar";
        default:
            return role;
    }
}

const UsersModal = ({ isOpen, onClose, customerId }: { isOpen: boolean; onClose: () => void; customerId: string }) => {
  const [users, setUsers] = useState<User[]>(initialUsers);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // nuevo estado para modal de delete
  const [userToDelete, setUserToDelete] = useState<User | null>(null); // usuario seleccionado para eliminar
  const [isLoading, setIsLoading] = useState(false); // <-- nuevo estado de loader
  const [isApiLoading, setIsApiLoading] = useState(false); // <--- ESTE ES EL NUEVO LOADER DE TRANSICIÓN
  const token = localStorage.getItem("authLisualToken") || "";
  const [tokenRole, setTokenRole] = useState<string>("");
  const [isPwdModalOpen, setIsPwdModalOpen] = useState(false);
  const [userForPassword, setUserForPassword] = useState<User | null>(null);
  const [obras, setObras] = useState<Obra[]>([]);



 useEffect(() => {
    const currentToken = localStorage.getItem("authLisualToken");
    //console.log("Current Token:", currentToken);

    if (currentToken) {
        try {
            // Usa jwtDecode para un manejo seguro
            const decodedToken = jwtDecode(currentToken) as { role?: string }; 
            setTokenRole(decodedToken.role || ""); 
            
        } catch (error) {
            console.error("Error al decodificar el token con jwt-decode:", error);
            // El token está expirado, es inválido, etc.
            setTokenRole(""); 
        }
    } else {
        setTokenRole("");
    }
}, [token]);
  

  // Usamos el customerId que se pasa como prop, no el de useParams (ya que estamos en un modal)
  // Pero mantenemos el uso de useParams para consistencia si es necesario. Si el customerId viene de una prop, usaremos la prop.
  // const { customerId } = useParams(); 
  
  
  useEffect(() => {
      // Solo cargar si el modal principal está abierto
      if (isOpen && customerId) {
          getUsers(customerId);
      }
  }, [isOpen, customerId]);

  useEffect(() => {
    if (!isOpen || !customerId) return;
    getObras(customerId)
      .then((data) => setObras(data || []))
      .catch((error) => console.error("Error al obtener las obras:", error));
  }, [isOpen, customerId]);

  const obraNombreById = useMemo(
    () => Object.fromEntries(obras.map((obra) => [obra.id, obra.nombre])),
    [obras]
  );



const getUsers = async (customerId: string): Promise<void> => {
    console.log("Fetching users for customerId:", customerId);
    setIsLoading(true);
    
    try {
      const response = await getUsersByCustomerId(customerId); 
      if (!response) {
        setUsers([]); 
        return;
      }
      setUsers(response);
    } catch (error) {
      console.error("Error al obtener los usuarios:", error);
      toast.error("Error al cargar la lista de usuarios.");
    } finally {
      setIsLoading(false);
    }
}



  const handleCreate = () => {
    setEditingUser(undefined);
    setIsUserModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsUserModalOpen(true);
  };

  // ✨ Lógica principal para interactuar con la API
  const handleConfirm = async (user: User) => {
    
    let result;
    const isUpdating = !!user.id; // Determinamos si es update si el usuario tiene un ID
setIsApiLoading(true); // <-- EMPEZAR EL LOADER DE TRANSICIÓN
    try {
      if (isUpdating) {
        // --- UPDATE ---
        console.log(`Actualizando usuario ID: ${user.id}`);
        // Llamada a la API de actualización. Enviamos el ID y los datos actualizados.
        result = await updateUser(user.id!, user);
        toast.success(`Usuario ${user.name} actualizado exitosamente.`);
      } else {
        // --- CREAR ---
        console.log(`Creando nuevo usuario: ${user.email}`);
        // Al crear, es crucial que se envíe el customerId
        const userToCreate = { ...user, customerId: customerId }; 
        try{
          result = await createUser(userToCreate);
          toast.success(`Usuario ${user.name} creado exitosamente.`);
        } catch (error) {
          if (axios.isAxiosError(error) && error.response) {
            const toastId = toast.error(error.response.data.error || "Error desconocido", {
              autoClose: 10000
            });
            setTimeout(() => toast.dismiss(toastId), 20000);
          } else {
            const err = error as Error;
            toast.error(err.message || "Error desconocido");
          }
        }
        //result = await createUser(userToCreate);
        
      }

      // 1. Cerrar el modal de edición/creación
      setIsApiLoading(false); // <-- DETENER EL LOADER DE TRANSICIÓN
      setIsUserModalOpen(false);
      setEditingUser(undefined);

      // 2. Recargar la lista de usuarios desde el backend para reflejar los cambios
      await getUsers(customerId); 

    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
                                        const toastId = toast.error(error.response.data.error || "Error desconocido", {
                                            autoClose: 10000
                                        });
                                        setTimeout(() => toast.dismiss(toastId), 20000);
                                    } else {
                                        const err = error as Error;
                                        toast.error(err.message || "Error desconocido");
                                    }
      console.error(`Error al ${isUpdating ? 'actualizar' : 'crear'} usuario:`, error);
      //toast.error(`Error al ${isUpdating ? 'actualizar' : 'crear'} el usuario. Inténtalo de nuevo.`);
      setIsApiLoading(false); // Asegurar que se detiene en caso de error
    }
  };

  // Al tocar el tacho, abrir modal de confirmación
  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  }

  // 2. Función para abrir el modal de password
  const handleResetPassword = (user: User) => {
    setUserForPassword(user);
    setIsPwdModalOpen(true);
  };

  // Confirmar eliminación: llamar a la API deleteUser
  const confirmDelete = async () => {
    
    if (!userToDelete || !userToDelete.id) {
      toast.error("No se pudo eliminar: usuario inválido.");
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      return;
    }

    try {
      
      await deleteUser(userToDelete.id, tokenRole);
      toast.success(`Usuario ${userToDelete.name} eliminado.`);
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      await getUsers(customerId);
    } catch (error) {
        console.error(error);
        if (axios.isAxiosError(error) && error.response) {
            const toastId = toast.error(error.response.data.error || "Error desconocido", {
                autoClose: 10000
            });
            setTimeout(() => toast.dismiss(toastId), 20000);
        } else {
            const err = error as Error;
            toast.error(err.message || "Error desconocido");
        }
    }
    
  }

  // Cancelar eliminación
  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  }


  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-0 md:p-4 z-40 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-none md:rounded-xl w-full h-full md:h-auto max-w-6xl md:mt-16 md:mb-8 shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">👥 Gestión de Usuarios</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 active:text-gray-800 min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center text-2xl md:text-xl touch-manipulation"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(100vh-8rem)] md:max-h-[70vh] -webkit-overflow-scrolling-touch">
          <div className="flex justify-end mb-4 md:mb-6">
            <button
              onClick={handleCreate}
              className="w-full md:w-auto flex items-center justify-center px-4 py-3 md:py-2 text-white rounded-lg font-semibold transition-colors duration-200 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 shadow-md min-h-[44px] touch-manipulation">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Crear usuario
            </button>
          </div>
{isLoading ? (
              <div className="flex flex-col items-center justify-center p-8 md:p-12">
                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                <span className="mt-3 text-sm text-gray-600">Cargando usuarios...</span>
              </div>
            ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {users.map((u) => (
                <div key={u.id || u.email} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">{u.name} {u.surname}</h3>
                      <p className="text-sm text-gray-600 truncate">{u.email}</p>
                    </div>
                    <div className="flex gap-2 ml-2">
                      <button
                        onClick={() => handleEdit(u)}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-full bg-blue-100 text-blue-600 active:bg-blue-200 transition duration-150 touch-manipulation"
                        aria-label="Editar usuario"
                      >
                        <SquarePen className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(u)}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-full bg-red-100 text-red-600 active:bg-red-200 transition duration-150 touch-manipulation"
                        aria-label="Eliminar usuario"
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                      u.role === 'guest' ? 'bg-indigo-100 text-indigo-800' :
                      u.role === 'responsibleUser' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {getRoleLabel(u.role)}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-3 h-3 rounded-full ${u.active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-xs text-gray-600">{u.active ? 'Activo' : 'Inactivo'}</span>
                    </div>
                    {u.phoneNumber && (
                      <span className="text-xs text-gray-500">{u.phoneNumber}</span>
                    )}
                    {toObraArray(u.obra).length > 0 && (
                      <span className="text-xs text-gray-500">
                        🏗️ {toObraArray(u.obra).map((id) => obraNombreById[id]).filter(Boolean).join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block overflow-x-auto bg-white shadow-lg rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                      Teléfono
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                      Obra
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Activo
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {users.map((u) => (
                    <tr key={u.id || u.email} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {u.name} {u.surname}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{u.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden lg:table-cell">{u.phoneNumber || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                         <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              u.role === 'guest' ? 'bg-indigo-100 text-indigo-800' :
                              u.role === 'responsibleUser' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                          }`}>
                              {getRoleLabel(u.role)}
                          </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden lg:table-cell">
                        {toObraArray(u.obra).map((id) => obraNombreById[id]).filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                         <div className={`w-4 h-4 mx-auto rounded-full ${u.active ? 'bg-green-500' : 'bg-red-500'}`} title={u.active ? 'Activo' : 'Inactivo'}></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex justify-center space-x-2">
                              <button
                                  onClick={() => handleEdit(u)}
                                  className="p-3 lg:p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition duration-150"
                                  title="Editar"
                              >
                                  <SquarePen className="w-5 h-5" />
                              </button>
                              <button
                                  onClick={() => handleResetPassword(u)}
                                  className="p-3 lg:p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition duration-150"
                                  title="Cambiar contraseña"
                              >
                                  <KeyRound className="w-5 h-5" />
                              </button>
                              <button
                                  onClick={() => handleDelete(u)}
                                  className="p-3 lg:p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition duration-150"
                                  title="Eliminar"
                              >
                                  <Trash className="w-5 h-5" />
                              </button>
                          </div>
                      </td>
                      
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t p-4 bg-gray-50 rounded-b-none md:rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full md:w-auto px-6 py-3 md:py-2 bg-gray-300 text-gray-800 font-medium rounded-lg hover:bg-gray-400 active:bg-gray-500 transition duration-150 min-h-[44px] md:min-h-0 touch-manipulation"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Loader de API (Asegúrate de que este loader esté presente y con un z-index alto) */}
      {isApiLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"> {/* z-index alto */}
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-2xl">
            <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span className="mt-4 text-md font-medium text-gray-800">Procesando...</span>
          </div>
        </div>
      )}

      {/* Modal de crear/editar */}
      <UserModal
isOpen={isUserModalOpen && !isApiLoading}        
onClose={() => {
          setIsUserModalOpen(false);
          setEditingUser(undefined);
        }}
        userData={editingUser}
        onConfirm={handleConfirm}

      />

      {/* 3. Renderizar el nuevo modal de Contraseña */}
      {userForPassword && (
        <ChangePasswordModal
          isOpen={isPwdModalOpen}
          onClose={() => {
            setIsPwdModalOpen(false);
            setUserForPassword(null);
          }}
          userId={userForPassword.id || ""}
          userName={`${userForPassword.name} ${userForPassword.surname}`}
        />
      )}

      {/* Modal de confirmación de eliminación */}
      {isDeleteModalOpen && userToDelete && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && cancelDelete()}
        >
          <div className="bg-black bg-opacity-40 absolute inset-0"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6 z-60">
            <h3 className="text-lg font-semibold mb-2">Confirmar eliminación</h3>
            <p className="text-sm text-gray-700 mb-6">
              ¿Confirmas eliminar el usuario <span className="font-medium">{userToDelete.name} {userToDelete.surname}</span>?
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition min-h-[44px] touch-manipulation"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition min-h-[44px] touch-manipulation"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersModal;